import { AlertCircle, AlertTriangle, Check, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useClipboard } from '@/hooks/use-clipboard';
import { type ApiErrorResponse, getStatusTitle, isServerError } from '@/lib/api-error';
import { cn } from '@/lib/utils';

interface ErrorDialogProps {
	error: ApiErrorResponse | null;
	open: boolean;
	onClose: () => void;
}

export function ErrorDialog({ error, open, onClose }: ErrorDialogProps) {
	const { t } = useTranslation();
	const { copied, copy, reset: resetClipboard } = useClipboard();

	if (!error) return null;

	const statusTitle = getStatusTitle(error.status);
	const isServer = isServerError(error.status);

	const handleCopyErrorDetails = () => {
		const parts = [`Error ${error.status}: ${error.title}`, ``, `Type: ${error.type}`, `Instance: ${error.instance}`];

		if (error.requestUrl) {
			parts.push(``, `Request URL:`, error.requestUrl);
		}

		if (error.requestPayload) {
			parts.push(``, `Request Payload:`, error.requestPayload);
		}

		parts.push(``, `Full Error:`, error.fullError, ``, `Stack Trace:`, error.stackTrace);

		copy(parts.join('\n'));
	};

	const handleOpenChange = (isOpen: boolean) => {
		if (isOpen) return;

		onClose();
		resetClipboard();
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="w-full max-w-full md:max-w-2xl">
				<DialogHeader>
					<div className="flex flex-col items-center gap-4">
						<div
							className={cn(
								'flex items-center justify-center rounded-full p-2',
								isServer ? 'bg-red-500/10' : 'bg-orange-500/10',
							)}>
							{isServer ? (
								<AlertCircle className="size-5 text-red-500" />
							) : (
								<AlertTriangle className="size-5 text-orange-500" />
							)}
						</div>
						<div className="flex items-center gap-2">
							<span
								className={cn(
									'rounded-md px-2 py-0.5 text-xs font-bold',
									isServer ? 'bg-red-500/10 text-red-500' : 'bg-orange-500/10 text-orange-500',
								)}>
								{error.status}
							</span>
							<DialogTitle className="text-left font-mono text-xl leading-6 font-medium">{statusTitle}</DialogTitle>
						</div>
						<span className="self-start text-sm text-muted-foreground">{error.title}</span>
					</div>
				</DialogHeader>

				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<span className="text-xs font-medium text-muted-foreground">{t('ErrorDialog.ErrorType')}</span>
						<code className="bg-muted/50 p-2.5 font-sans text-xs font-medium">{error.type}</code>
					</div>

					{(error.stackTrace || error.fullError) && (
						<div className="flex flex-col gap-2">
							<span className="text-xs font-medium text-muted-foreground">{t('ErrorDialog.StackTrace')}</span>
							<div className="max-h-60 overflow-auto bg-muted/50 p-3.5">
								<pre className="font-sans text-xs leading-4.5 break-all whitespace-pre-wrap">
									{error.fullError || error.stackTrace}
								</pre>
							</div>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="ghost" size="sm" onClick={handleCopyErrorDetails} className="gap-2">
						{copied ? <Check className="size-4 text-green-500" /> : <Copy className="size-4" />}
						<span className="text-xs font-normal">{copied ? t('ErrorDialog.Copied') : t('ErrorDialog.Copy')}</span>
					</Button>
					<Button className="font-semibold" onClick={onClose}>
						{t('ErrorDialog.Close')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
